import { Component, effect, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';

import { AuthService } from 'src/app/services/auth.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { RegistrarService } from 'src/app/services/registrar.service';
import { SharedService } from 'src/app/services/shared.service';

import { CookieService } from 'ngx-cookie-service';
import { ExternalHost } from 'src/app/types/external-host';
import { ScopeServer } from 'src/app/types/scope-server';
import { LaunchDialogComponent } from '../launch-dialog/launch-dialog.component';

/**
 * scope card component
 *
 * consists of two states: either logged-in or not
 * when not logged it shows a login button
 * otherwise a button to launch scope client is shown
 * @class ScopeCardComponent
 * @typedef {ScopeCardComponent}
 */
@Component( {
    selector: 'app-scope-card',
    templateUrl: './scope-card.component.html',
    styleUrls: ['./scope-card.component.scss'],
    standalone: false
} )
export class ScopeCardComponent {
    /** reference on list of scope servers, assigend to users organisation */
    servers: ScopeServer[] = [];

    /**
     * reference on selected server
     * in case of multiple servers, defaults to first in list
     */
    selectedServer!: ScopeServer;

    /** flag, if community map is shown in build */
    showCommunityMap = environment.optionalModules.showCommunityMap;
    showHostRegions = environment.optionalModules.showHostRegions;

    region = '';

    authService = inject( AuthService );
    #dialog = inject( MatDialog );
    #localStorageService = inject( LocalStorageService );
    #registrarService = inject( RegistrarService );
    #sharedService = inject( SharedService );
    #cookieService = inject( CookieService );

    cards = [
        {
            action: true,
            centered: true,
            image: 'assets/image-scope-card.svg',
            text: $localize`:@@login-card-message:Log in to launch the desktop application.`
        },
        {
            action: true,
            centered: true,
            image: 'assets/image-scope-card.svg'
        }
    ];

    constructor() {
        this.region = this.#cookieService.get( 'scope.region' ) || 'EUROPE';

        effect( () => {
            if ( this.authService.isLoggedIn() ) {
                const account = this.#localStorageService.load( 'account' );
                if ( account && account.organizationOID ) {
                    this.loadScopeServers( account.organizationOID );
                } else {
                    this.#sharedService.handleSessionExpired();
                }
            }
        } );
    }

    /**
     * log in helper function
     * calls login in {@link AuthService}
     */
    login() {
        this.authService.login();
    }

    /**
     * load assigned servers
     * to be used in launch app card select
     * when preferred region is set, select the appropriate alternate host
     * @param {string} oid organization id
     */
    loadScopeServers( oid: string ) {
        this.#registrarService.getScopeServer( oid )
            .subscribe( ( scopeServers ) => {
                this.servers = scopeServers;

                if ( scopeServers.length === 1 ) {
                    this.selectedServer = scopeServers[0];
                }
            } );
    }

    /**
     * helper function to open {@link LaunchDialogComponent} modal
     *
     * passes data set to modal component
     * @property action : name of action to be taken
     * @property url : custom startUrl of scope client instance
     * @property externalHostname : external hostname of selected scope server
     */
    launchScope() {
        // select alternate host based on region setting
        if ( this.selectedServer.alternativeHostnames && this.selectedServer.alternativeHostnames.length ) {
            let hosts: string[] = [];
            this.selectedServer.alternativeHostnames.forEach( host => {
                hosts.push( host.region );
            } );
            this.#cookieService.set( 'scope.regions', hosts.join() );

            const regionalServer = this.selectedServer.alternativeHostnames
                .filter( ( host: ExternalHost ) => host.region === this.region )[0];
            Object.assign( this.selectedServer, regionalServer );
        }

        const data = {
            action: 'launch-app',
            externalHostname: this.selectedServer!.externalHostname,
            version: this.selectedServer!.scopeVersion,
            url: this.selectedServer!.startUrl,
        };

        this.#sharedService.checkTokenValidity()
            .then( () =>
                this.#dialog.open(
                    LaunchDialogComponent,
                    { data, panelClass: 'riege-dialog' }
                )
            ).catch( () => {
                this.#sharedService.prepareSessionRenewal();
            } );
    }
}
